import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from "@mui/icons-material/Delete";
import { Checkbox } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { useDealPortfolioStore, usePipedriveStore } from "../../..";
import useDealPortfolioCreate from "../../../hooks/useDealPortfolioCreate";
import useDealPortfolioDelete from "../../../hooks/useDealPortfolioDelete";
import useDealPortfolioUpdate from "../../../hooks/useDealPortfolioUpdate";
import useNavigateToDeal from "../../../hooks/useNavigateToDeal";
import IDeal from "../../../interfaces/IDeal";
import IDealPortfolio from "../../../interfaces/IDealPortfolio";
import commonStyles from "../../Common.module.css";
import { DeleteControl } from "../../EditControls";
import inputStyles from "../../inputs/Input.module.css";
import InputWithSearch from "../../inputs/InputWithSearchFormikEntitySelect";
import Modal from "../../Modal";
import styles from "./DealPortfolioPopup.module.css";

function DealPortfolioPopup({ dealId, portfolio, onClose }: IDealPortfolioPopupProps) {
    const isEditMode = !!portfolio;
    const pipedriveStore = usePipedriveStore();
    const dealPortfolioStore = useDealPortfolioStore();
    const createPortfolio = useDealPortfolioCreate();
    const updatePortfolio = useDealPortfolioUpdate();
    const deletePortfolio = useDealPortfolioDelete();
    const navigateToDeal = useNavigateToDeal();

    const [name, setName] = useState(portfolio?.name ?? "");
    const [dealIds, setDealIds] = useState<number[]>(portfolio ? portfolio.dealIds : [dealId]);
    const [checkedDealIds, setCheckedDealIds] = useState<number[]>([]);
    const [searchValue, setSearchValue] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const currentDeals = useMemo(
        () => dealIds.map(id => pipedriveStore.deals.find(d => d.id === id)).filter((d): d is IDeal => !!d),
        [pipedriveStore.deals, dealIds]
    );

    const searchResults = useMemo(() => {
        const trimmedSearchQuery = searchQuery.trim().toLowerCase();
        const items = trimmedSearchQuery
            ? pipedriveStore.deals.filter(d => {
                const dealPortfolio = dealPortfolioStore.getPortfolioForDeal(d.id);
                return !dealIds.includes(d.id)
                    && (!dealPortfolio || dealPortfolio.id === portfolio?.id)
                    && d.name?.toLowerCase().includes(trimmedSearchQuery);
            })
            : [];
        return items.slice(0, 20);
    }, [pipedriveStore.deals, dealIds, searchQuery, dealPortfolioStore.portfolios, portfolio]);

    const isValid = name.trim().length > 0 && dealIds.length >= 2;

    const handleSubmit = async () => {
        if (!isValid)
            return;

        if (isEditMode)
            await updatePortfolio({ ...portfolio, name: name.trim(), dealIds });
        else
            await createPortfolio({ name: name.trim(), dealIds });
        onClose();
    };

    const handleDelete = async () => {
        if (!portfolio)
            return;

        await deletePortfolio(portfolio.id);
        onClose();
    };

    return (
        <Modal
            header={isEditMode ? "Edit Portfolio" : "Create Portfolio"}
            midWidth
            onClose={onClose}
        >
            <div className={styles.container}>
                <div className={inputStyles.container}>
                    <label>Portfolio Name</label>
                    <input
                        placeholder="Portfolio name..."
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                </div>

                <div>
                    <div className={styles.sectionLabel}>Deals in Portfolio</div>
                    <div className={styles.dealList}>
                        {!currentDeals.length && <div className={styles.emptyList}>No deals added yet</div>}
                        {currentDeals.map(deal => (
                            <div key={deal.id} className={styles.dealRow}>
                                <Checkbox
                                    size="small"
                                    checked={checkedDealIds.includes(deal.id)}
                                    onChange={e => setCheckedDealIds(ids =>
                                        e.target.checked ? [...ids, deal.id] : ids.filter(i => i !== deal.id)
                                    )}
                                />
                                <span className={styles.dealName}>{deal.name}</span>
                                <button
                                    className={styles.navigateButton}
                                    title="Go to deal"
                                    onClick={() => navigateToDeal(deal.id, true)}
                                >
                                    <ArrowForwardIcon fontSize="small" />
                                </button>
                                <DeleteIcon
                                    fontSize="small"
                                    className={commonStyles.clickableIcon}
                                    onClick={() => {
                                        setDealIds(ids => ids.filter(i => i !== deal.id));
                                        setCheckedDealIds(ids => ids.filter(i => i !== deal.id));
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                    {!!checkedDealIds.length && (
                        <div className={commonStyles.controls}>
                            <div className={commonStyles.controlGroup} />
                            <div className={commonStyles.controlGroup}>
                                <div className={commonStyles.button} onClick={() => {
                                    setDealIds(ids => ids.filter(i => !checkedDealIds.includes(i)));
                                    setCheckedDealIds([]);
                                }}>
                                    <DeleteIcon fontSize="small" className={commonStyles.clickableIcon} /> Delete Selected
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className={inputStyles.container}>
                    <label>Add Deal</label>
                    <InputWithSearch<IDeal>
                        value={searchValue}
                        placeholder="Search deals..."
                        debouncedOnChange={setSearchQuery}
                        searchResult={{ query: searchQuery, items: searchResults }}
                        getKey={d => d.id}
                        getItemText={d => d.name ?? ""}
                        onValueChange={setSearchValue}
                        onSelect={deal => {
                            setDealIds(ids => [...ids, deal.id]);
                            setSearchValue("");
                            setSearchQuery("");
                        }}
                    />
                </div>

                <div className={[commonStyles.controls, commonStyles.modalControls].join(" ")}>
                    <div className={commonStyles.controlGroup}>
                        {isEditMode && (
                            <DeleteControl
                                entityName="Portfolio"
                                isLoading={dealPortfolioStore.isPortfoliosLoading.deleting!}
                                onDelete={handleDelete}
                            />
                        )}
                    </div>
                    <div className={commonStyles.controlGroup}>
                        <button className={commonStyles.secondaryButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className={commonStyles.primaryButton}
                            disabled={!isValid || dealPortfolioStore.isPortfoliosLoading.creating! || dealPortfolioStore.isPortfoliosLoading.updating!}
                            onClick={handleSubmit}
                        >
                            {isEditMode ? "Save" : "Create Portfolio"}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default observer(DealPortfolioPopup);

interface IDealPortfolioPopupProps {
    dealId: number;
    portfolio: IDealPortfolio | null;
    onClose: () => void;
}
